import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Bell, CheckCircle, Info, WarningCircle } from "@phosphor-icons/react";

const ICONS = { info: Info, success: CheckCircle, warning: WarningCircle, alert: WarningCircle, error: WarningCircle };
const COLORS = { info: "#002FA7", success: "#00A86B", warning: "#F5A623", alert: "#F5A623", error: "#FF3B30" };

function ago(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function NotificationBell({ tone = "dark" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const unread = items.filter((n) => !n.read).length;

  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      await api.post("/notifications/mark-read").catch(() => {});
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    }
  };

  const iconColor = tone === "light" ? "#fff" : "#555";

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell" onClick={toggle} className="relative flex items-center transition-transform hover:scale-110">
        <Bell size={20} weight={unread > 0 ? "fill" : "regular"} color={iconColor} />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-2 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-3 w-80 max-h-[420px] overflow-y-auto bg-white border border-black/10 rounded-sm shadow-xl z-50">
          <div className="px-4 py-3 border-b border-black/10 flex items-center justify-between sticky top-0 bg-white">
            <span className="font-head font-bold text-sm">Notifications</span>
            <span className="text-xs text-[#888]">{items.length} total</span>
          </div>
          {items.length === 0 ? (
            <p className="p-6 text-center text-sm text-[#888]">You're all caught up 🎉</p>
          ) : (
            items.map((n, i) => {
              const Icon = ICONS[n.type] || Info;
              return (
                <div key={n.id || i} data-testid={`notif-item-${i}`} className={`px-4 py-3 border-b border-black/5 flex gap-3 ${n.read ? "" : "bg-[#002FA7]/[0.04]"}`}>
                  <Icon size={18} weight="fill" color={COLORS[n.type] || "#002FA7"} className="mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm leading-snug">{n.title}</p>
                    <p className="text-[11px] text-[#999] mt-0.5">{ago(n.at)}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
